import React from 'react';
import { Stack, Button, CircularProgress, Box } from '@mui/material';

/**
 * FormActions component using Material-UI
 * This component provides a consistent wrapper for form action buttons
 */
const FormActions = ({
  primaryAction,
  secondaryAction,
  tertiaryAction,
  alignment = 'right', // 'left' | 'center' | 'right' | 'space-between'
  spacing = 'md', // 'sm' | 'md' | 'lg'
  variant = 'default', // 'default' | 'sticky' | 'card'
  className = '',
  children,
  ...props
}) => {
  // Map spacing to MUI spacing values
  const getSpacing = () => {
    switch (spacing) {
      case 'sm':
        return 1;
      case 'lg':
        return 3;
      case 'md':
      default:
        return 2;
    }
  };

  // Map alignment to flexbox properties
  const getJustifyContent = () => {
    switch (alignment) {
      case 'left':
        return 'flex-start';
      case 'center':
        return 'center';
      case 'space-between':
        return 'space-between';
      case 'right':
      default:
        return 'flex-end';
    }
  };

  // Get variant styles
  const getVariantStyles = () => {
    switch (variant) {
      case 'sticky':
        return {
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          p: 2,
          mt: 3,
          zIndex: 'appBar',
        };
      case 'card':
        return {
          backgroundColor: 'background.subtle',
          borderRadius: 1,
          p: 2,
          mt: 3,
        };
      case 'default':
      default:
        return {
          mt: 3,
        };
    }
  };

  // Helper function to render action button
  const renderActionButton = (action, type) => {
    if (!action) return null;

    const {
      label,
      onClick,
      loading = false,
      disabled = false,
      variant: buttonVariant = type === 'primary' ? 'contained' : 'outlined',
      color = type === 'primary' ? 'primary' : 'inherit',
      size = 'medium',
      startIcon,
      endIcon,
      type: buttonType = type === 'primary' ? 'submit' : 'button',
      ...buttonProps
    } = action;

    return (
      <Button
        key={`${type}-action`}
        type={buttonType}
        variant={buttonVariant}
        color={color}
        size={size}
        onClick={onClick}
        disabled={disabled || loading}
        startIcon={loading ? <CircularProgress size={16} /> : startIcon}
        endIcon={endIcon}
        sx={{
          // Mobile-first responsive styling
          width: { xs: '100%', sm: 'auto' },
          minWidth: { sm: 120 },
        }}
        {...buttonProps}
      >
        {label}
      </Button>
    );
  };

  // If children are provided, render them instead of action buttons
  if (children) {
    return (
      <Box
        className={className}
        sx={{
          ...getVariantStyles(),
        }}
        {...props}
      >
        {children}
      </Box>
    );
  }

  // Determine button order based on alignment
  const getButtonOrder = () => {
    if (alignment === 'left') {
      return [
        renderActionButton(primaryAction, 'primary'),
        renderActionButton(secondaryAction, 'secondary'),
        renderActionButton(tertiaryAction, 'tertiary')
      ].filter(Boolean);
    } else {
      // For right, center, and space-between alignments, secondary comes first
      return [
        renderActionButton(tertiaryAction, 'tertiary'),
        renderActionButton(secondaryAction, 'secondary'),
        renderActionButton(primaryAction, 'primary')
      ].filter(Boolean);
    }
  };

  const buttons = getButtonOrder();

  if (buttons.length === 0) {
    return null;
  }

  return (
    <Box
      className={className}
      sx={{
        ...getVariantStyles(),
      }}
      {...props}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={getSpacing()}
        justifyContent={getJustifyContent()}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        {buttons}
      </Stack>
    </Box>
  );
};

export default FormActions;